<template>
  <div
    ref="interactiveListItem"
    :class="bem({ 'background-': props.background, 'width-': props.width })"
    style="--item-progress-in: 1; --item-progress-out: 0"
  >
    <div :class="bem('inner')">
      <div :class="bem('media-container')">
        <MediaItem
          :item="item.media"
          aspect-ratio="auto"
          :class="bem('media')"
        />
      </div>
      <div :class="bem('content')">
        <VisionHeading
          v-if="props.item.subtitle"
          tag="h6"
          size="h6"
          :class="bem('subtitle')"
        >
          {{ props.item.subtitle }}
        </VisionHeading>

        <VisionHeading
          v-if="props.item.title"
          tag="h4"
          size="h3"
          :class="bem('title')"
        >
          {{ props.item.title }}
        </VisionHeading>

        <p :class="bem('description')">
          {{ props.item.description }}
        </p>
      </div>
      <div
        :class="bem('button-container')"
        @click="handleClick"
      >
        <VisionIconButton
          icon="plus"
          size="x-small"
          fake-button
          :class="bem('icon-button')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const bem = useBEM('InteractiveListItem')
const { gsap } = useGsap()
const { $track } = useNuxtApp() as any

const interactiveListItem = ref<HTMLElement | null>(null)

interface InteractiveListItemProps {
  item: {
    title: string
    subtitle: string
    description: string
    media: any
  }
  background: string
  width?: 'full' | 'narrow'
}

const props = withDefaults(defineProps<InteractiveListItemProps>(), {
  width: 'full'
})

const emits = defineEmits(['open-overlay'])

async function handleClick() {
  emits('open-overlay')

  await $track.global.linkClicked({
    interactionElement: props.item.subtitle,
    interactionCategory: 'Interactive List',
  }, 'Open Interactive List Item')
}

onMounted(() => {
  if (interactiveListItem.value) {
    gsap.set(interactiveListItem.value, {
      '--item-progress-in': 0,
    })

    gsap.to(interactiveListItem.value, {
      scrollTrigger: {
        trigger: interactiveListItem.value,
        start: 'top bottom',
        end: 'top 85%',
        scrub: 1,
      },
      '--item-progress-in': 1,
      ease: 'none',
    })
    gsap.to(interactiveListItem.value, {
      scrollTrigger: {
        trigger: interactiveListItem.value,
        start: 'top top',
        end: 'top -33%',
        scrub: 0,
      },
      '--item-progress-out': 1,
      ease: 'none',
    })
  }
})
</script>

<style lang="postcss">
.InteractiveListItem {
  @apply relative lg:sticky top-0 bg-white py-5 md:py-10 border-t border-black border-opacity-[0.16]
  origin-top;
  transform: scale(calc(1.05 - (0.05 * var(--item-progress-in))));

  &:after {
    @apply md:content-[''] absolute inset-0 bg-white
    pointer-events-none;
    opacity: clamp(0, var(--item-progress-out), 0.9);
  }

  &:last-child:after {
    @apply opacity-0;
  }
}

.InteractiveListItem__inner {
  @apply relative grid gap-5 sm:gap-8 md:gap-14 global-wrapper-large;
  grid-template-columns: minmax(120px, 40%) 1fr;
}

.InteractiveListItem__button-container {
  @apply absolute right-3 sm:right-[var(--outer-gutter)] h-full
  flex items-center justify-center cursor-pointer;

  @screen md {
    @apply right-auto bottom-0 left-[var(--outer-gutter)] h-fit
    p-2 pt-4 pr-4 cursor-pointer;
  }
}

.InteractiveListItem__icon-button {
  --vision-icon-button-bg-colour: theme('colors.grey.800');

  @screen smMax {
    --vision-icon-size: 12px;
    @apply p-2.5;
  }
}

.InteractiveListItem__media-container {
  @apply relative aspect-[5/4];
}

.InteractiveListItem__media {
  @apply h-full w-full origin-top-right;
}

.InteractiveListItem__content {
  @apply w-full gap-3 sm:gap-5 md:gap-6 py-2 grid self-center pr-8 sm:pr-24 md:pr-0;

  @screen xl {
    @apply grid-cols-2 h-fit self-start;
  }
}

.InteractiveListItem__title {
  @apply text-base md:text-lg xl:text-xl xxl:text-2xl full:text-3xl;
}

.InteractiveListItem__subtitle {
  @apply font-fragment-mono text-grey-600
  row-[2] md:row-auto
  xl:col-span-2;
}

.InteractiveListItem__description {
  @apply hidden md:block text-sm font-bold text-grey-700 mt-0 xl:mt-2;
}

/* Modifiers
  --background
  --width
*/
.InteractiveListItem--background-grey,
.InteractiveListItem--background-light-to-grey {
  @apply bg-grey-300;
}

.InteractiveListItem--background-dark {
  @apply bg-black text-white border-white border-opacity-[0.16];

  &::after {
    @apply bg-black;
  }

  .InteractiveListItem__description {
    @apply text-grey;
  }
}

.InteractiveListItem--width-narrow .InteractiveListItem__inner {
  @apply global-wrapper;
}
</style>
